import React from 'react';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Icon } from 'components/shared/Icon';
import { Typography } from 'components/shared/Typography';

import { Tooltip } from 'components/Tooltip';

import { SessionListItem } from '../SessionListItem';

import type { SessionDayGroup } from '../../TimelineSection.helpers';

import styles from './SessionList.module.scss';

const SessionList = ({
  listRef,
  selectedSessionId,
  sessionsCount,
  sessionGroups,
  onSessionSelect,
}: {
  listRef: React.RefObject<HTMLDivElement>;
  selectedSessionId: string | null;
  sessionsCount: number;
  sessionGroups: SessionDayGroup[];
  onSessionSelect: (sessionId: string) => void;
}) => {
  useTranslation();

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Typography variant="body1" weight="semiBold" mode="compact">
            {getTranslation('student_page.journey.list.title')}
          </Typography>
          <Typography
            className={styles.count}
            variant="body1"
            weight="medium"
            mode="compact"
          >
            {sessionsCount}
          </Typography>
        </div>
        {/* What a session is + the J / K shortcut, out of the way of the list. */}
        <Tooltip
          text={getTranslation('student_page.journey.list.info')}
          triggerClassName={styles.infoTrigger}
          triggerElement={
            <span
              className={styles.info}
              role="img"
              aria-label={getTranslation('student_page.journey.list.info')}
            >
              <Icon className={styles.infoIcon} name="info" />
            </span>
          }
        />
      </div>

      <div className={styles.list} ref={listRef}>
        {sessionGroups.length === 0 ? (
          <Typography className={styles.empty} variant="body2">
            {getTranslation('student_page.journey.list.filter_empty')}
          </Typography>
        ) : (
          sessionGroups.map((group) => (
            <div key={group.dayKey} className={styles.day}>
              <Typography
                className={styles.dayLabel}
                variant="body3"
                weight="medium"
                mode="compact"
              >
                {group.label}
              </Typography>
              {group.sessions.map((session) => (
                <SessionListItem
                  key={session.id}
                  selected={session.id === selectedSessionId}
                  session={session}
                  onSelect={onSessionSelect}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default React.memo(SessionList) as any;
